import { useEffect, useState } from "react";
import {
  BellRing,
  Clock,
  Eye,
  Mail,
  MonitorSmartphone,
  Pill,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { NotificationKind, PreferenceDTO } from "@shared/types/notifications";
import { isNotificationKindVisible } from "@shared/constants/notifications";
import { useToast } from "../lib/toast/ToastProvider";
import { ApiError } from "../lib/api/client";
import {
  useNotificationPreferences,
  useUpdatePreferences,
} from "../lib/api/useNotificationPreferences";
import { cn } from "../lib/cn";
import { FOCUS_RING } from "../lib/focusRing";
import type { AccentName } from "../lib/accents";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { SectionCard } from "../components/ui/SectionCard";
import { IconChip } from "../components/ui/IconChip";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";

type Channel = "inApp" | "email";
type DraftState = Record<NotificationKind, Record<Channel, boolean>>;

interface KindMeta {
  kind: NotificationKind;
  title: string;
  description: string;
  icon: LucideIcon;
  accent: AccentName;
}

const ALL_KINDS: KindMeta[] = [
  {
    kind: "link_opened",
    title: "Link opened",
    description: "When a caregiver first opens a profile you shared.",
    icon: Eye,
    accent: "violet",
  },
  {
    kind: "link_unacknowledged",
    title: "Link not acknowledged",
    description:
      "When a caregiver hasn't confirmed they've read a profile after a while.",
    icon: Clock,
    accent: "rose",
  },
  {
    kind: "medication_expiring",
    title: "Medication expiring",
    description: "When a child's medication is approaching its expiration date.",
    icon: Pill,
    accent: "amber",
  },
  {
    kind: "allergy_plan_incomplete",
    title: "Allergy plan incomplete",
    description:
      "When a severe allergy has no rescue medication linked, or the device is expiring.",
    icon: TriangleAlert,
    accent: "rose",
  },
];

/**
 * The toggles this page shows: every kind a surface can currently deliver.
 * The health reminders drop out while HEALTH_REMINDERS_ENABLED is off (the
 * same shared list the server uses to keep them out of the feed), and their
 * stored preferences are neither shown nor sent, so they survive untouched.
 */
const KINDS: KindMeta[] = ALL_KINDS.filter(({ kind }) => isNotificationKindVisible(kind));

const CHANNELS: { channel: Channel; label: string; icon: LucideIcon }[] = [
  { channel: "inApp", label: "In-app", icon: MonitorSmartphone },
  { channel: "email", label: "Email", icon: Mail },
];

/** Fold the API's array into a lookup keyed by kind, defaulting to both-on. */
function toDraft(preferences: PreferenceDTO[]): DraftState {
  const draft = {} as DraftState;
  for (const { kind } of ALL_KINDS) {
    draft[kind] = { inApp: true, email: true };
  }
  for (const p of preferences) {
    draft[p.kind] = { inApp: p.inApp, email: p.email };
  }
  return draft;
}

function draftDiffers(draft: DraftState, preferences: PreferenceDTO[]): boolean {
  const server = toDraft(preferences);
  return KINDS.some(
    ({ kind }) =>
      draft[kind].inApp !== server[kind].inApp ||
      draft[kind].email !== server[kind].email,
  );
}

/** Accessible on/off switch (keyboard + screen-reader via role="switch"). */
function ToggleSwitch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-50",
        FOCUS_RING,
        // Off: a neutral-500 outline (4.7:1), since neutral-400 is under the 3:1 control bar.
        checked ? "border-sky-600 bg-sky-600" : "border-neutral-500 bg-neutral-200",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow-soft transition",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  );
}

export function NotificationSettings() {
  const { data, isLoading, isError, error, refetch, isRefetching } =
    useNotificationPreferences();
  const update = useUpdatePreferences();
  const { publish } = useToast();

  const [draft, setDraft] = useState<DraftState | null>(null);

  // Initialize the editable draft once, so a background refetch never clobbers
  // in-progress edits. onSave re-syncs explicitly from the mutation response.
  useEffect(() => {
    if (data && draft === null) setDraft(toDraft(data.preferences));
  }, [data, draft]);

  const toggle = (kind: NotificationKind, channel: Channel) => {
    setDraft((prev) =>
      prev
        ? { ...prev, [kind]: { ...prev[kind], [channel]: !prev[kind][channel] } }
        : prev,
    );
  };

  const dirty = draft != null && data != null && draftDiffers(draft, data.preferences);

  const onSave = async () => {
    if (!draft) return;
    const payload = KINDS.map(({ kind }) => ({
      kind,
      inApp: draft[kind].inApp,
      email: draft[kind].email,
    }));
    try {
      const result = await update.mutateAsync(payload);
      setDraft(toDraft(result.preferences));
      publish({ variant: "success", title: "Notification preferences saved" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't save preferences",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <AppShell maxWidth="3xl">
      <PlayfulHero
        accent="violet"
        decor="compact"
        art="controls"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        eyebrow="Settings"
        title="Notification preferences"
        description="Choose how you want to hear about activity on your children's profiles. New kinds start with everything on."
      />

      <div className="mt-8 sm:mt-10">
        {isLoading || (draft === null && !isError) ? (
          <SectionCard as="div" icon={BellRing} title="Delivery preferences" accent="violet">
            <div role="status" className="flex items-center gap-2.5 text-sm text-neutral-600">
              <Spinner size="sm" /> Loading your preferences…
            </div>
          </SectionCard>
        ) : isError || draft === null ? (
          <EmptyState
            icon={BellRing}
            title="Couldn't load your preferences"
            description={
              error instanceof ApiError
                ? error.message
                : "Something went wrong. Please try again."
            }
            action={
              <Button variant="secondary" isLoading={isRefetching} onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <SectionCard
            as="div"
            icon={BellRing}
            title="Delivery preferences"
            accent="violet"
          >
            <ul className="space-y-3">
              {KINDS.map(({ kind, title, description, icon, accent }) => (
                <li
                  key={kind}
                  className="flex flex-col gap-4 rounded-2xl bg-white/75 p-4 ring-1 ring-white sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <IconChip icon={icon} accent={accent} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-900">{title}</p>
                      <p className="mt-0.5 text-xs text-neutral-600">{description}</p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-5 pl-[3.25rem] sm:pl-0">
                    {CHANNELS.map(({ channel, label, icon: ChannelIcon }) => (
                      <div key={channel} className="flex items-center gap-2">
                        <ChannelIcon
                          className="h-4 w-4 text-neutral-500"
                          aria-hidden="true"
                        />
                        <span className="text-sm text-neutral-700" aria-hidden="true">
                          {label}
                        </span>
                        <ToggleSwitch
                          checked={draft[kind][channel]}
                          onChange={() => toggle(kind, channel)}
                          disabled={update.isPending}
                          label={`${label} notifications for ${title}`}
                        />
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center justify-end gap-3">
              <span
                aria-live="polite"
                className={cn(
                  "text-xs text-neutral-600 transition-opacity",
                  dirty ? "opacity-100" : "opacity-0",
                )}
              >
                You have unsaved changes
              </span>
              <Button
                onClick={onSave}
                disabled={!dirty}
                isLoading={update.isPending}
              >
                Save changes
              </Button>
            </div>
          </SectionCard>
        )}
      </div>
    </AppShell>
  );
}
